import { ThunkDispatch } from "core-integration/src/store";
import { getElementsById } from "core-integration/src/store/elements/reducers";
import {
  showElementSummary,
  updateElementTypeControl,
} from "core-integration/src/store/elements/thunks";
import { IElement } from "core-integration/src/store/elements/types";
import { getNextEvent } from "core-integration/src/store/events/reducers";
import { getRules } from "core-integration/src/store/game/reducers";
import { resetLastChange } from "core-integration/src/store/squad/actions";
import {
  canAutocomplete,
  canReset,
  getErrors,
  getLastChange,
  getProposedElements,
  getSavedPicks,
  getSquadMode,
} from "core-integration/src/store/squad/reducers";
import {
  autoComplete,
  removeElement,
  reset,
  restoreElement,
} from "core-integration/src/store/squad/thunks";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import React, { useEffect, useState } from "react";
import { Trans, withTranslation, WithTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "rebass/styled-components";
import styled from "styled-components";
import { createInteractionData } from "../../utils/tealium";
import Alert, { AlertWrap } from "../Alert";
import BoldLinkButton from "../BoldLinkButton";
import Button from "../Button";
import Dialog, { DialogButtonItem } from "../Dialog";
import DialogManager from "../DialogManager";
import { ControlArrowEnd } from "../icons/Arrows";
import { BoxWrapStyles } from "../Utils";
import AutoPickDialog from "./AutoPickDialog";
import Scoreboard from "./Scoreboard";
import SquadActionBar from "./SquadActionBar";

const TableWrap = styled.div`
  ${BoxWrapStyles};
`;

const PlayerListButtonWrap = styled.div`
  display: inline-flex;
  justify-content: flex-end;
  width: 100%;
  padding: 0 0.63rem;

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    width: 33%;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    display: none;
  }
`;

const PlayerActions = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    width: 50%;
    margin: 0 auto;
  }
`;

//TODO: Move IListTableProps somewhere more abstract?
export interface IListTableProps {
  elementType: number;
  key: string;
  positions: number[];
  renderElementDialog: (element: IElement) => void;
  renderElementMenu: (position: number) => void;
  showSidebar: () => void;
}

interface IOwnProps {
  listTable: (listTableProps: IListTableProps) => React.ReactNode;
  scoreboard: string;
  showSidebar: () => void;
  submitDialog: (handleHide: () => void) => React.ReactNode;
  showListTableAsDefault?: boolean;
  contentTitle: string;
  showListRef: React.RefObject<HTMLButtonElement>;
  replacePlayerRef: React.RefObject<HTMLButtonElement>;
}

type Props = IOwnProps & WithTranslation;

const CreateSquad = ({
  contentTitle,
  listTable,
  scoreboard,
  showSidebar,
  showListTableAsDefault,
  submitDialog,
  showListRef,
  replacePlayerRef,
  t,
}: Props) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const [positionForMenu, setPositionForMenu] = useState<number>(0);
  const [showAutoPickDialog, setShowAutoPickDialog] = useState<boolean>(false);
  const elementsById = useSelector(getElementsById);
  const errors = useSelector(getErrors);
  const lastChange = useSelector(getLastChange);
  const mode = useSelector(getSquadMode);
  const proposedElements = useSelector(getProposedElements);
  const savedPicks = useSelector(getSavedPicks);
  const canAutocompleteTeam = useSelector(canAutocomplete);
  const canResetTeam = useSelector(canReset);
  const rules = useSelector(getRules);
  const teamsById = useSelector(getTeamsById);

  const autoPick = () => {
    if (!dispatch(autoComplete())) {
      // We should do something :-)
      window.console.log("Failed to autocomplete");
    }
  };

  const handleActionBarAutoPick = () => {
    createInteractionData({
      contentTitle,
      interactionId: "nba-fantasy-game:auto-pick-roster:button",
      interactionText: t("scoreboard.autoPick", "Auto Pick"),
    });
    autoPick();
  };

  const handleActionBarReset = () => {
    createInteractionData({
      contentTitle,
      interactionId: "nba-fantasy-game:reset-roster:button",
      interactionText: t("reset", "Reset"),
    });
    dispatch(reset());
  };

  const handleShowMenuForElement = (position: number) =>
    setPositionForMenu(position);

  const handleHideMenuForElement = () => {
    setPositionForMenu(0);
  };

  const handleRemoveElement = (position: number) => {
    handleHideMenuForElement();
    dispatch(removeElement(position));
  };

  const handleRestoreElement = (position: number) => {
    handleHideMenuForElement();
    dispatch(restoreElement(position));
  };

  const showElementType = (elementTypeId: number, position: number) => {
    handleRemoveElement(position);
    dispatch(updateElementTypeControl(elementTypeId));
    showSidebar();
  };

  const showDialog = (element: IElement) => {
    dispatch(showElementSummary(element.id));
    handleHideMenuForElement();
  };

  const handleShowAutoPickDialog = () => {
    setShowAutoPickDialog(true);
  };

  const handleHideAutoPickDialog = () => {
    setShowAutoPickDialog(false);
  };

  const renderMenu = () => {
    let element: IElement | undefined;
    let mode: "remove" | "restore" | undefined;
    if (proposedElements[positionForMenu]) {
      element = proposedElements[positionForMenu];
      mode = "remove";
    } else if (savedPicks[positionForMenu]) {
      element = elementsById[savedPicks[positionForMenu].element];
      mode = "restore";
    }
    if (!mode || !element) {
      return null;
    }
    return (
      <Dialog closeDialog={handleHideMenuForElement}>
        <Dialog.Header closeDialog={handleHideMenuForElement}>
          {`${element.first_name} ${element.second_name}`}
        </Dialog.Header>
        <Dialog.Body>
          <PlayerActions>
            {mode === "remove" ? (
              <>
                <DialogButtonItem>
                  <Button
                    variant="primary"
                    onClick={() => handleRemoveElement(positionForMenu)}
                    fullwidth="true"
                  >
                    {t("createSquad.removePlayer", "Remove player")}
                  </Button>
                </DialogButtonItem>
                <DialogButtonItem>
                  <Button
                    variant="primary"
                    onClick={() =>
                      showElementType(element!.element_type, positionForMenu)
                    }
                    fullwidth="true"
                  >
                    {t("createSquad.replacePlayer", "Select replacement")}
                  </Button>
                </DialogButtonItem>
              </>
            ) : mode === "restore" ? (
              <>
                <DialogButtonItem>
                  <Button
                    onClick={() => handleRestoreElement(positionForMenu)}
                    fullwidth="true"
                    variant="secondary"
                  >
                    {t("createSquad.restorePlayer", "Restore Player")}
                  </Button>
                </DialogButtonItem>
                <DialogButtonItem>
                  <Button
                    ref={replacePlayerRef}
                    onClick={() =>
                      showElementType(element!.element_type, positionForMenu)
                    }
                    fullwidth="true"
                  >
                    {t("createSquad.replacePlayer", "Select Replacement")}
                  </Button>
                </DialogButtonItem>
              </>
            ) : (
              ""
            )}
            <DialogButtonItem>
              <Button
                onClick={() => showDialog(element as IElement)}
                fullwidth="true"
                variant="secondary"
              >
                {t("createSquad.viewInformation", "View Information")}
              </Button>
            </DialogButtonItem>
          </PlayerActions>
        </Dialog.Body>
      </Dialog>
    );
  };

  useEffect(() => {
    dispatch(resetLastChange());
    if (Object.keys(proposedElements).length === 0) {
      handleShowAutoPickDialog();
    }
  }, [dispatch, proposedElements]);

  interface IHandleAutoPickProps {
    interactionText: string;
    action: () => void;
  }

  const handleAutoPick = ({
    action,
    interactionText,
  }: IHandleAutoPickProps) => {
    createInteractionData({
      contentTitle: "roster selection",
      interactionId: "nba-fantasy-game:roster-selection:button",
      interactionText: interactionText,
    });
    action();
    handleHideAutoPickDialog();
  };

  if (!rules) {
    return null;
  }

  let latestAction: React.ReactNode = null;
  if (lastChange.type === "addition") {
    latestAction = (
      <Alert type="success">
        <Trans
          i18nKey="createSquad.alert.elementAddedn"
          values={{ playerName: elementsById[lastChange.element].web_name }}
        >
          <strong>{elementsById[lastChange.element].web_name}</strong> has been
          added to your roster.
        </Trans>
      </Alert>
    );
  } else if (lastChange.type === "removal") {
    latestAction = (
      <Alert type="success">
        <Trans
          i18nKey="createSquad.alert.elementRemovedn"
          values={{ playerName: elementsById[lastChange.element].web_name }}
        >
          <strong>{elementsById[lastChange.element].web_name}</strong> has been
          removed from your roster.
        </Trans>
      </Alert>
    );
  }

  // Button enablers
  const canEnter = !Object.keys(errors).length;

  const sharedSquadListTableProps = (
    pos: number[],
    elementType: number,
    key: string,
  ) => ({
    elementType,
    key,
    positions: pos,
    renderElementDialog: showDialog,
    renderElementMenu: handleShowMenuForElement,
    showSidebar,
  });

  const submitButtonText = {
    selection: t("createSquad.enterSquad", "Enter roster"),
    transfers: t("createSquad.makeTransfers", "Make Transactions"),
  };

  const positionsByElementType: {
    [index: string]: number[];
  } = {
    1: [1, 2, 3, 4, 5],
    2: [6, 7, 8, 9, 10],
  };

  const showTabsInReverseOrder = showListTableAsDefault;

  const nextEvent = useSelector(getNextEvent);

  return (
    <div>
      <Scoreboard scoreboard={scoreboard} />
      {errors.overTeamLimit && (
        <AlertWrap>
          <Alert type="error">
            {t(
              "createSquad.errors.overTeamLimit",
              "Too many players selected from",
            )}{" "}
            <strong>
              {errors.overTeamLimit
                .map((team) => teamsById[team].name)
                .join(", ")}
            </strong>
          </Alert>
        </AlertWrap>
      )}
      {latestAction && <AlertWrap>{latestAction}</AlertWrap>}
      <Box mt={4}>
        {[
          <Box mb={4} mx="-1px">
            <TableWrap>
              {Object.keys(positionsByElementType)
                .reverse()
                .map((et) =>
                  listTable(
                    sharedSquadListTableProps(
                      positionsByElementType[et],
                      parseInt(et, 10),
                      et,
                    ),
                  ),
                )}
            </TableWrap>
          </Box>,
        ].sort(() => (showTabsInReverseOrder ? 1 : -1))}
      </Box>
      <PlayerListButtonWrap>
        <BoldLinkButton
          ref={showListRef}
          onClick={showSidebar}
          label={t("squadBase.playerList", "View Player List")}
          icon={<ControlArrowEnd color="blueDark" />}
          variant="secondary"
        />
      </PlayerListButtonWrap>

      {renderMenu()}
      {nextEvent && (
        <DialogManager
          render={(showDialog, handleShow, handleHide) => (
            <>
              <SquadActionBar>
                {["roster selection", "transactions"].includes(
                  contentTitle,
                ) && (
                  <Button
                    onClick={handleActionBarAutoPick}
                    disabled={!canAutocompleteTeam}
                    fullwidth="true"
                  >
                    {t("scoreboard.autoPick", "Auto Pick")}
                  </Button>
                )}
                {["roster selection", "transactions"].includes(
                  contentTitle,
                ) && (
                  <Button
                    type="reset"
                    onClick={handleActionBarReset}
                    variant="secondary"
                    disabled={!canResetTeam}
                    fullwidth="true"
                  >
                    {t("reset", "Reset")}
                  </Button>
                )}
                <Button
                  onClick={handleShow}
                  disabled={!canEnter}
                  fullwidth="true"
                  variant="primary"
                >
                  {submitButtonText[mode]}
                </Button>
              </SquadActionBar>
              {showDialog && submitDialog(handleHide)}
            </>
          )}
        />
      )}

      {showAutoPickDialog && (
        <AutoPickDialog
          handleHideAutoPickDialog={handleHideAutoPickDialog}
          handleAutoPick={handleAutoPick}
          autoPick={autoPick}
        />
      )}
    </div>
  );
};

export default withTranslation()(CreateSquad);
